/**
 * Priority-driven column hiding for ResponsiveTableView.
 *
 * Each column carries an optional `priority`:
 *   1 (or unset) — always visible, even on a phone-sized card list header
 *   2            — visible from the md breakpoint (>= 768px)
 *   3            — visible from the lg breakpoint (>= 1024px)
 *
 * Pure module — JSX/React-free so the filtering decision runs under the
 * `node --test` strip-types sweep. The matchMedia wiring lives in
 * `useResponsiveColumns` (responsive-table-view.tsx).
 */

export type ColumnPriority = 1 | 2 | 3;

/** Breakpoint bucket derived from the viewport (mobile-first). */
export type ResponsiveBreakpoint = 'sm' | 'md' | 'lg';

/** Minimal shape `filterColumnsByPriority` needs — any column type qualifies. */
export interface PrioritizedColumn {
  id: string;
  priority?: ColumnPriority;
}

/** Highest column priority still visible at a given breakpoint. */
export function maxVisiblePriority(breakpoint: ResponsiveBreakpoint): ColumnPriority {
  switch (breakpoint) {
    case 'lg':
      return 3;
    case 'md':
      return 2;
    default:
      return 1;
  }
}

/** Resolve the breakpoint bucket from md/lg media-query matches. */
export function resolveBreakpoint(isMd: boolean, isLg: boolean): ResponsiveBreakpoint {
  if (isLg) return 'lg';
  if (isMd) return 'md';
  return 'sm';
}

/**
 * Keep only the columns whose priority fits the breakpoint. Columns without
 * a priority are treated as priority 1 (always visible). Order is preserved.
 */
export function filterColumnsByPriority<C extends PrioritizedColumn>(
  columns: readonly C[],
  breakpoint: ResponsiveBreakpoint,
): C[] {
  const max = maxVisiblePriority(breakpoint);
  return columns.filter((c) => (c.priority ?? 1) <= max);
}
